import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { X, Camera } from "lucide-react";

// Hand-picked bento layout for 9 items. Each row entry is a Tailwind class
// applied to the tile. The pattern loops if more than 9 items exist.
const BENTO = [
    "md:col-span-2 md:row-span-2",  // 0 — large
    "md:col-span-1 md:row-span-1",  // 1
    "md:col-span-1 md:row-span-1",  // 2
    "md:col-span-1 md:row-span-1",  // 3
    "md:col-span-1 md:row-span-1",  // 4
    "md:col-span-2 md:row-span-2",  // 5 — large
    "md:col-span-2 md:row-span-1",  // 6 — wide
    "md:col-span-1 md:row-span-2",  // 7 — tall
    "md:col-span-1 md:row-span-1",  // 8
];

const CATEGORY_STYLES = {
    "Digital Literacy": "bg-[#1E3A8A] text-white",
    "Education": "bg-[#2D5A27] text-white",
    "Community Development": "bg-[#E8A365] text-white",
    "Community Outreach": "bg-[#0F766E] text-white",
    "Child Wellbeing": "bg-[#B45309] text-white",
    "Women Empowerment": "bg-[#7C2D12] text-white",
    default: "bg-black/80 text-white",
};

const chipClass = (cat) => CATEGORY_STYLES[cat] || CATEGORY_STYLES.default;

export default function Gallery() {
    const [items, setItems] = useState([]);
    const [activeCat, setActiveCat] = useState("All");
    const [preview, setPreview] = useState(null);

    useEffect(() => { api.get("/gallery").then((r) => setItems(r.data || [])); }, []);

    const categories = useMemo(
        () => ["All", ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))],
        [items]
    );
    const filtered = activeCat === "All" ? items : items.filter((i) => i.category === activeCat);

    // Category → count map for pill display
    const countMap = useMemo(() => {
        const m = { All: items.length };
        for (const i of items) m[i.category] = (m[i.category] || 0) + 1;
        return m;
    }, [items]);

    return (
        <div data-testid="gallery-page">
            {/* Hero */}
            <section className="pt-20 pb-10 md:pt-32 md:pb-14">
                <div className="container-hero grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-7">
                        <div className="label-eyebrow text-brand-forest mb-4 flex items-center gap-2">
                            <Camera size={14} strokeWidth={1.75} /> Gallery
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl font-semibold leading-[1.05]">
                            Moments, faces, and days at work.
                        </h1>
                        <p className="text-slate-600 text-lg mt-5 max-w-xl">
                            Every photo is a village visit, a classroom, a workshop. Real people,
                            real progress — from our team across Bundelkhand.
                        </p>
                    </div>
                    <div className="md:col-span-5 grid grid-cols-3 gap-3">
                        <div className="bg-brand-forest text-white rounded-2xl p-5">
                            <div className="font-display text-4xl font-semibold">{items.length}</div>
                            <div className="text-xs uppercase tracking-[0.15em] text-white/80 mt-1">Photos</div>
                        </div>
                        <div className="bg-white border border-black/5 rounded-2xl p-5">
                            <div className="font-display text-4xl font-semibold text-brand-forest">
                                {Object.keys(countMap).length - 1}
                            </div>
                            <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mt-1">Programmes</div>
                        </div>
                        <div className="bg-brand-stone rounded-2xl p-5">
                            <div className="font-display text-4xl font-semibold text-brand-forest">85+</div>
                            <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mt-1">Villages</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Filter pills */}
            <section className="pb-8">
                <div className="container-hero flex flex-wrap gap-2">
                    {categories.map((c) => (
                        <button
                            key={c}
                            onClick={() => setActiveCat(c)}
                            data-testid={`gallery-filter-${c}`}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 border ${
                                activeCat === c
                                    ? "bg-brand-forest text-white border-brand-forest"
                                    : "bg-white border-black/10 text-slate-700 hover:border-brand-forest hover:text-brand-forest"
                            }`}
                        >
                            {c} <span className={`ml-1 text-[10px] opacity-70`}>{countMap[c] || 0}</span>
                        </button>
                    ))}
                </div>
            </section>

            {/* Uniform grid — all tiles same size for a balanced look */}
            <section className="pb-24 md:pb-32">
                <div className="container-hero">
                    <div
                        data-testid="gallery-bento"
                        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-5"
                    >
                        {filtered.map((g, i) => (
                            <button
                                key={g.id}
                                onClick={() => setPreview(g)}
                                data-testid={`gallery-item-${i}`}
                                className="group relative overflow-hidden rounded-2xl bg-brand-stone focus:outline-none focus:ring-2 focus:ring-brand-forest aspect-[4/3]"
                            >
                                <img
                                    src={g.image_url}
                                    alt={g.caption}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[900ms] ease-out"
                                />
                                {/* Category chip — always visible */}
                                <div className={`absolute top-3 left-3 z-10 text-[10px] uppercase tracking-[0.15em] font-semibold px-2.5 py-1 rounded-full ${chipClass(g.category)}`}>
                                    {g.category}
                                </div>
                                {/* Caption overlay — reveals on hover */}
                                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <div className="text-white font-display text-lg leading-tight">{g.caption}</div>
                                </div>
                            </button>
                        ))}
                    </div>

                    {filtered.length === 0 && (
                        <div className="text-center text-slate-500 py-24">No photos in this category yet.</div>
                    )}
                </div>
            </section>

            {/* Lightbox */}
            {preview && (
                <div
                    onClick={() => setPreview(null)}
                    data-testid="lightbox"
                    className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-6"
                >
                    <button
                        onClick={() => setPreview(null)}
                        aria-label="Close"
                        className="absolute top-6 right-6 text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
                    >
                        <X />
                    </button>
                    <div onClick={(e) => e.stopPropagation()} className="max-w-5xl w-full">
                        <img
                            src={preview.image_url}
                            alt={preview.caption}
                            className="w-full h-auto max-h-[78vh] object-contain rounded-lg"
                        />
                        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                            <span className={`text-[11px] uppercase tracking-[0.15em] font-semibold px-3 py-1 rounded-full ${chipClass(preview.category)}`}>
                                {preview.category}
                            </span>
                            <div className="text-white/95 font-display text-xl md:text-2xl leading-tight">
                                {preview.caption}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
